/**
 *
 * Organizations/Show/ContactCustomFields
 *
 */

import React, { memo, useState, useMemo } from 'react'

// NPM Libraries
import ReactTooltip from 'react-tooltip'
import FloatingLabel from '@joeyparis/react-floating-label-input'
import styled from '@emotion/styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// Graphql
import { gql, useMutation } from '@apollo/client'

// Componentes
import Modal from 'components/Modal'

// Utils
import { COLORS } from 'global-styles'

const CREATE_CUSTOM_FIELD = gql`
	mutation createContactCustomField($organization_id: ID!, $field: String!, $options: [String]) {
		createContactCustomField(input: { organization_id: $organization_id, field: $field, options: $options }) {
			contact_custom_field {
				id
			}
		}
	}
`

const UPDATE_CUSTOM_FIELD = gql`
	mutation updateContactCustomField($id: ID!, $field: String!, $options: [String]) {
		updateContactCustomField(input: { id: $id, field: $field, options: $options }) {
			contact_custom_field {
				id
			}
		}
	}
`

const DELETE_CUSTOM_FIELD = gql`
	mutation deleteContactCustomField($id: ID!) {
		deleteContactCustomField(input: { id: $id }) {
			contact_custom_field {
				id
			}
		}
	}
`

const Table = styled.table`
	width: 100%;
	border: 1px solid ${COLORS.grey_e};

	th {
		font-size: 0.9rem;
		padding: 8px 12px;
		border: 1px solid ${COLORS.grey_e};
		border-left: 0;
		border-right: 0;
	}

	tbody {
		min-height: 400px;
		tr:hover {
			background-color: ${COLORS.lightGrey};

			.actions {
				visibility: visible;
			}
		}
	}

	td {
		padding: 8px 12px;
		border-bottom: 1px solid ${COLORS.grey_e};

		.field-col {
			display: block;
			max-width: 280px;
			white-space: nowrap;
			overflow: hidden !important;
			text-overflow: ellipsis;
		}

		.options {
			display: flex;
			align-items: center;

			.list {
				display: block;
				max-width: 300px;
				white-space: nowrap;
				overflow: hidden !important;
				text-overflow: ellipsis;
			}
		}

		.actions {
			width: 100px;
			display: flex;
			justify-content: end;
			visibility: hidden;

			.btn {
				padding: 2px 7px;
			}
		}
	}
`

function CustomFieldForm({ field, setField, handleClose, organization_id }) {
	const [createCustomField, { loading: creating }] = useMutation(CREATE_CUSTOM_FIELD, {
		onCompleted: () => setField(null),
	})

	const [updateCustomField, { loading: updating }] = useMutation(UPDATE_CUSTOM_FIELD, {
		onCompleted: () => setField(null),
	})

	const handleSubmit = (e) => {
		e.preventDefault()

		const variables = { ...field }

		variables.options = variables.options.filter((o) => Boolean(o))

		if (variables.id) {
			updateCustomField({ variables })
		} else {
			createCustomField({ variables: { ...variables, organization_id } })
		}
	}

	const loading = creating || updating

	return (
		<Modal show modalDidClose={handleClose} style={{ width: 700 }}>
			<h4>{field.id ? 'Edit' : 'Create'} Contact Custom Field</h4>

			<div className="form-group row">
				<div className="col-5">
					<FloatingLabel
						type="text"
						label="Custom Field Name"
						name="key"
						value={field.field}
						onChange={(e) => setField({ ...field, field: e.target.value })}
						required={field.values?.some((value) => value?.length > 0)}
						prevent_autofill
						disabled={loading}
					/>
				</div>
				<div className="col-6">
					{field.options.map((option, index) => (
						<div className="d-inline-block w-100" key={index}>
							<FloatingLabel
								type="text"
								label={`Option ${index + 1}`}
								name="value"
								value={option}
								onChange={(e) => {
									const new_options = [...field.options]
									new_options[index] = e.target.value

									setField({ ...field, options: new_options })
								}}
								onKeyPress={(e) => {
									if (e.key === 'Enter' && e.target.value !== '') {
										const newOptions = [...field.options, '']
										setField({ ...field, options: newOptions })
									}
								}}
								disabled={loading}
								prevent_autofill
							/>
						</div>
					))}
				</div>

				<div className="col-1 p-0">
					<div className="h-100 d-flex align-items-end">
						<button
							type="button"
							onClick={() => setField({ ...field, options: [...field.options, ''] })}
							className="btn-unstyled align-self-end text-muted my-1"
							style={{ verticalAlign: 'bottom' }}
							disabled={loading}
						>
							<div className="fa-layers text-center" style={{ width: 30, height: 30 }}>
								<FontAwesomeIcon icon="circle" size="2x" style={{ opacity: '0.2' }} />
								<FontAwesomeIcon icon="plus" />
							</div>
						</button>
					</div>
				</div>
			</div>

			<button onClick={handleSubmit} className="btn btn-primary float-right mt-2">
				{field.id ? 'Edit' : 'Create'} Contact Custom Field
			</button>
		</Modal>
	)
}

function CustomFieldRow({ field, onEdit, onDelete, loading }) {
	const options_list = useMemo(() => {
		if (!field) return ''

		return field?.options.join(', ')
	}, [field])

	return (
		<tr>
			<td>
				<span className="field-col">{field.field}</span>
			</td>
			<td>
				<div className="options">
					<span className="list">{options_list}</span>

					{options_list.length >= 40 && (
						<React.Fragment>
							<FontAwesomeIcon
								data-tip
								data-for={`${field.id}-responses`}
								icon="info-circle"
								className="text-dark ml-1"
							/>

							<ReactTooltip id={`${field.id}-responses`} className="unzoom" place="right" effect="float">
								<p className="mb-2">Answer Options:</p>

								<ul className="mb-2 pl-4">
									{field.options.map((response, index) => (
										<li key={index}>{response}</li>
									))}
								</ul>
							</ReactTooltip>
						</React.Fragment>
					)}
				</div>
			</td>

			<td style={{ width: '100px' }}>
				<div className="actions">
					<button onClick={() => onEdit(field)} className="btn btn-warning btn-sm" disabled={loading}>
						Edit
					</button>

					<button
						onClick={() => {
							swal({
								title: 'Are you sure you want to delete this Contact Custom Field?',
								type: 'warning',
								showCancelButton: true,
								confirmButtonText: 'Yes, delete this Contact Custom Field!',
								cancelButtonText: 'Cancel',
								confirmButtonClass: 'btn btn-danger d-inline',
								cancelButtonClass: 'btn btn-default d-inline',
								buttonsStyling: false,
							}).then(
								() => onDelete(),
								() => {},
							)
						}}
						className="btn btn-danger btn-sm"
						disabled={loading}
					>
						Delete
					</button>
				</div>
			</td>
		</tr>
	)
}

function ContactCustomFields({ organization: { contact_custom_fields, id } }) {
	const [field, setField] = useState(null)

	const [deleteCustomField] = useMutation(DELETE_CUSTOM_FIELD, {
		onCompleted: () => setField(null),
	})

	return (
		<div>
			<p className="mt-2">
				Create Custom Fields that appear within every Contact across all Users that belong to the Organization.
				The field name and corresponding value will populate inside the "Details" box within each Contact
				record.
			</p>

			<Table>
				<thead>
					<tr>
						<th style={{ width: 250 }}>Field Name</th>
						<th>Options</th>
						<th aria-label="empty" />
					</tr>
				</thead>

				{contact_custom_fields?.length > 0 && (
					<tbody>
						{contact_custom_fields.map((custom_field) => (
							<CustomFieldRow
								field={custom_field}
								onEdit={() => setField(custom_field)}
								onDelete={() => deleteCustomField({ variables: { id: custom_field.id } })}
							/>
						))}
					</tbody>
				)}
			</Table>

			<div className="container p-0"></div>

			{field && (
				<CustomFieldForm
					field={field}
					setField={setField}
					handleClose={() => setField(null)}
					organization_id={id}
				/>
			)}

			<div className="d-flex justify-content-between mt-3">
				<button
					type="button"
					className="btn btn-secondary rounded"
					onClick={() => setField({ field: '', options: [''] })}
				>
					<FontAwesomeIcon icon="plus" className="mr-2" />
					Add Custom Field
				</button>
			</div>
		</div>
	)
}

ContactCustomFields.propTypes = {}

export default memo(ContactCustomFields)
